import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { useClickOutside } from '../../../../hooks'
import { Checkbox } from '../../../shared/NarusMaterial'
import { useIGAO } from '../../context'
import { DatapickerUrgent, SaveOnPress } from '../../shared'
import {
  prepareStatus,
  notEditableItem,
  isFavorable,
  cancelledOrCompleted,
  diffDate,
  itemDetails
} from '../../utils'
import { ActionIcons } from '../action-icons'
import { Edit } from '../selects'
import { Textarea } from '../textarea'

import { ActionDetails } from './action-details'
import styles from './action.module.scss'

export function Action ({
  action,
  handleKeyUp,
  handleKeyDown,
  item,
  issue,
  shiftDown,
  deleteAction,
  handleBatch,
  editId,
  setEditId,
  canSave,
  setCanSave,
  setSelectedIssue,
  selected
}) {
  const { pageData: { user, lists }, batch, editAction, masterBatch } = useIGAO()
  const [ variableParams, setVariableParams ] = useState({
    status: action.details.find(rel => rel.key === 'status').id,
    assignee: action.details.find(rel => rel.key === 'assignee').id,
    description: action.description,
    urgent: action.urgent,
    dueDate: action.dueDate
  })
  const [ changed, setChanged ] = useState([])
  const actionRef = useRef(null)
  const autoSaveRef = useRef(null)
  const stateRef = useRef()
  const editMode = editId === action.id
  stateRef.current = {
    variableParams,
    changed
  }
  stateRef.current.canSave = canSave

  const canSaveExp = act => {
    if (item.type === 'other') return false
    if (cancelledOrCompleted(act)) return false
    if (stateRef?.current?.variableParams.description.trim().length === 0) return true
    return (
      act.description.trim() ===
      stateRef?.current.variableParams?.description.trim()
    )
  }

  const canSaveAction =
    editMode &&
    !issue.actions.some(act => act.id !== action.id && canSaveExp(act))

  useEffect(() => {
    if (editMode) {
      setCanSave(canSaveAction)
    }
  }, [ canSaveAction, editMode, setCanSave ])

  const saveData = () => {
    const edited = k => stateRef.current.changed.includes(k)
    const changedData = Object.keys(stateRef.current.variableParams)?.reduce(
      (acc, k) => {
        if (edited(k)) acc[k] = stateRef.current.variableParams[k]
        return acc
      },
      {}
    )

    if (stateRef.current.canSave) {
      editAction(item.id, issue.id, action.id, changedData)
      setEditId(null)
    } else {
      toast.error('Can\'t create duplicate action', { position: 'bottom-center' })
    }
  }

  useClickOutside(actionRef, () => saveData())

  const addChanges = k => {
    setChanged(prev => {
      const clone = [ ...prev ]
      if (clone.includes(k)) return clone
      clone.push(k)
      return clone
    })
  }

  const editActionDetail = (k, id) => {
    const status = prepareStatus(k, id, user.id, variableParams.status)
    if (k === 'assignee') {
      addChanges('status')
    }
    setVariableParams(prev => ({
      ...prev,
      [k]: id,
      status
    }))
    addChanges(k)
  }

  const onChangeDate = date => {
    let dueDate
    try {
      dueDate = new Date(date).toISOString()
    } catch (err) {
      return
    }

    setVariableParams(prev => ({
      ...prev,
      dueDate
    }))
    addChanges('dueDate')
  }

  const notEditableAction = notEditableItem(action)

  const setEditableAction = e => {
    if (window.getSelection().toString().length) {
      return
    }
    if (e.target.type !== 'checkbox') {
      if (!notEditableAction && canSave) setEditId(action.id)
    }
  }

  const handleKeyPress = e => {
    if (e.keyCode === 13 || (e.charCode === 13 && !shiftDown)) {
      e.preventDefault()
      saveData()
    }
    if (e.keyCode === 27 || e.charCode === 27) {
      setVariableParams(() => ({
        description: action.description,
        urgent: action.urgent,
        dueDate: action.dueDate,
        status: action.details.find(rel => rel.key === 'status').id,
        assignee: action.details.find(rel => rel.key === 'assignee').id
      }))
      setEditId(null)
    }
  }

  const handleEditInput = value => {
    setVariableParams(prev => ({
      ...prev,
      description: value
    }))
    addChanges('description')
  }

  if (!variableParams) {
    return <div />
  }

  const values = {
    inlineDueDate: Math.ceil(
      Math.abs(
        diffDate(
          variableParams.dueDate,
          cancelledOrCompleted(action) ? action.statusChanged : null
        )
      )
    )
  }

  const onDelete = () => deleteAction(action.id)

  return (
    <div
      className={cx(styles.actionItem, {
        [styles.edit]: editMode,
        [styles.draft]: action.draft,
        [styles.notEditable]: notEditableAction
      })}
      ref={editMode ? actionRef : null}
      data-cy={`action${action.draft ? '-draft' : ''}`}
    >
      <SaveOnPress
        onKeyPress={e => handleKeyPress(e)}
        onKeyDown={e => handleKeyPress(e)}
        saveRef={autoSaveRef}
        editable={editMode}
      >
        <ActionContainer>
          <div className={cx('action-checkbox', styles.leftGroup, styles.checkbox, styles.content)}>
            <Checkbox
              disabled={cancelledOrCompleted(action)}
              value={masterBatch || batch.includes(action.id)}
              onChange={() => handleBatch(item.id, issue.id, action.id)}
            />
          </div>
          <div className={cx(styles.dueDate, { [styles.ended]: diffDate(variableParams.dueDate) < 0 })}>
            <span className={styles.date}>{values.inlineDueDate}</span>
          </div>
          <div className={styles.actionDescriptionEdit}>
            <div>
              {editMode && action.draft
                ? (
                  <Textarea
                    onKeyUp={handleKeyUp}
                    onKeyPress={e => handleKeyPress(e)}
                    onKeyDown={handleKeyDown}
                    onChange={value => handleEditInput(value)}
                    value={variableParams?.description}
                    placeholder='Free Text Action'
                  />
                )
                : (
                  <button
                    className={styles.description}
                    onClick={setEditableAction}
                  >
                    {variableParams?.description}
                  </button>
                )}
            </div>
          </div>

          {!editMode
            ? (
              <ActionDetails
                setEditableAction={setEditableAction}
                variableParams={variableParams}
                lists={lists}
                editable={editMode}
                action={action}
              />
            )
            : (
              <>
                {itemDetails()?.map(
                  detail =>
                    detail.type === 'select' && (
                      <div
                        className={styles.selects}
                        key={detail.key}
                      >
                        <Edit
                          relation={detail}
                          handler={editActionDetail}
                          disabled={isFavorable(item)}
                          selected={{
                            key: detail.key,
                            id: variableParams[detail.key]
                          }}
                          edit={editMode}
                          detail
                          currentUser={user.id}
                          currentAssignee={variableParams.assignee}
                          lists={lists}
                        />
                      </div>
                    )
                )}
                <DatapickerUrgent
                  value={variableParams.dueDate}
                  urgent={variableParams.urgent}
                  handleChangeUrgent={editActionDetail}
                  handleChangeDate={onChangeDate}
                />
              </>
            )}
          <ActionIcons
            className={styles.actionsIcon}
            onClick={setSelectedIssue}
            selected={selected}
            parentItem={issue}
            item={action}
            deleteFn={onDelete}
          />
        </ActionContainer>
      </SaveOnPress>
    </div>
  )
}

function ActionContainer ({ children, editMode }) {
  const { page, pageData: { metadata: { trend } } } = useIGAO()

  return (
    <div
      className={cx(styles.actionContainer, {
        [styles.editMode]: editMode,
        [styles.trend]: trend,
        [styles.careplan]: page === 'careplan',
        [styles.tasks]: page === 'tasks'
      })}
    >
      {children}
    </div>
  )
}
